import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";
import {
  QueryOperation,
  fetchAdminAuthControllerRefresh,
} from "./estabrookComponents";
import { Tokens } from "../types/models/tokens";
import { useAuthStore } from "../services/shared/auth/auth-store";
import { applyAuthResponse } from "../services/shared/auth/apply-auth-response";

export type EstabrookContext = {
  fetcherOptions: {
    /**
     * Headers to inject in the fetcher
     */
    headers?: {
      authorization?: string;
      "X-Esr-Guest-Access"?: string;
      "Content-Type"?: string;
    };
    /**
     * Query params to inject in the fetcher
     */
    queryParams?: {};
    /**
     * Refresh token if expired
     */
    refreshToken?: () => Promise<Tokens | null>;
  };
  queryOptions: {
    /**
     * Set this to `false` to disable automatic refetching when the query mounts or changes query keys.
     * Defaults to `true`.
     */
    enabled?: boolean;
  };
  /**
   * Query key manager.
   */
  queryKeyFn: (operation: QueryOperation) => QueryKey;
};

// Global refresh promise to prevent concurrent refresh attempts
let globalRefreshPromise: Promise<Tokens | null> | null = null;

/**
 * Context injected into every react-query hook wrappers
 *
 * @param queryOptions options from the useQuery wrapper
 */
export function useEstabrookContext<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  _queryOptions?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    "queryKey" | "queryFn"
  >,
): EstabrookContext {
  const { setIsLoggedIn } = useAuthStore();
  return {
    fetcherOptions: {
      headers: {},
      refreshToken: async () => {
        const logout = () => {
          Tokens.destroy();
          setIsLoggedIn(false);
          globalRefreshPromise = null; // Clear refresh promise on logout
          return null;
        };

        // If a refresh is already in progress, wait for it
        if (globalRefreshPromise) {
          return await globalRefreshPromise;
        }

        const tokens = Tokens.retrieveFromLocalStorage();
        if (!tokens) {
          return logout();
        }

        // Add buffer time (30 seconds) to prevent timing issues
        if (tokens.isExpiredWithBuffer(30)) {
          if (tokens.isExpired(true)) {
            return logout();
          }

          // Create and store the refresh promise
          globalRefreshPromise = (async () => {
            try {
              const data = await fetchAdminAuthControllerRefresh({
                body: {
                  refreshToken: tokens.refreshToken,
                },
              });

              if (data.data?.accessToken && data.data?.refreshToken) {
                return applyAuthResponse(data.data);
              } else {
                return logout();
              }
            } catch (error) {
              console.error("❌ Token refresh failed:", error);
              return logout();
            } finally {
              globalRefreshPromise = null; // Clear the promise when done
            }
          })();

          return await globalRefreshPromise;
        }

        return tokens;
      },
    },
    queryOptions: {},
    queryKeyFn,
  };
}

export const queryKeyFn = (operation: QueryOperation) => {
  const queryKey: unknown[] = hasPathParams(operation)
    ? operation.path
        .split("/")
        .filter(Boolean)
        .map((i) => resolvePathParam(i, operation.variables.pathParams))
    : operation.path.split("/").filter(Boolean);

  if (hasQueryParams(operation)) {
    queryKey.push(operation.variables.queryParams);
  }

  if (hasBody(operation)) {
    queryKey.push(operation.variables.body);
  }

  return queryKey;
};
// Helpers
const resolvePathParam = (key: string, pathParams: Record<string, string>) => {
  if (key.startsWith("{") && key.endsWith("}")) {
    return pathParams[key.slice(1, -1)];
  }
  return key;
};

const hasPathParams = (
  operation: QueryOperation,
): operation is QueryOperation & {
  variables: { pathParams: Record<string, string> };
} => {
  return Boolean((operation.variables as any).pathParams);
};

const hasBody = (
  operation: QueryOperation,
): operation is QueryOperation & {
  variables: { body: Record<string, unknown> };
} => {
  return Boolean((operation.variables as any).body);
};

const hasQueryParams = (
  operation: QueryOperation,
): operation is QueryOperation & {
  variables: { queryParams: Record<string, unknown> };
} => {
  return Boolean((operation.variables as any).queryParams);
};
